import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgePullPackLook = 'pull-pack' | 'pull-pack-badge';

const PULL_PACK_LOOKS = new Set<string>(['pull-pack', 'pull-pack-badge']);
export const isWedgePullPackLook = (look: string): look is WedgePullPackLook => PULL_PACK_LOOKS.has(look);

/* Released pull pack card (the cap-hit sheet and the Subscription Supply Drop grid): one native button per pack. It is a relative flex column with a
   4px gap, a 14px radius, 14px by 12px padding, left-aligned text and the surface fill. A pack with a badge draws a 1.5px accent border and any
   other pack a 0.5px separator border, so the badge card is 2px larger each way; the caller keeps that switch exact by choosing the look. It fades
   every property over 200ms on the Wedge ease. Held with `disabled` (the pack that is being bought) it dims to 55% and takes the wait cursor. Each
   reads the exact legacy roles and keeps the order of the declarations of the source. The caller owns the badge (it sits 8px above the card, so the
   card stays `position: relative`), the label and the lines of text.
   The look draws no transparent target, anchor or stacking context (`wedgeSettingsDrawsHit`, and a `content: none` rule): a card is at least 90px
   tall (the 14px padding twice and four lines of text) and half the width of the grid, and the cards tile 8px apart, so a 44px region would only
   repeat the box and would square its 14px corners. */
const PADDING = '14px 12px', GAP = 4, FADE_MS = 200, RADIUS = 14, BUSY_OPACITY = 0.55;

export function wedgePullPackStyle(look: WedgePullPackLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  const badge = look === 'pull-pack-badge';
  return {
    position: 'relative',
    background: r('surface'),
    border: `${badge ? '1.5px' : '0.5px'} solid ${badge ? r('accent') : r('separator')}`,
    borderRadius: RADIUS, padding: PADDING,
    textAlign: 'left', cursor: disabled ? 'wait' : 'pointer', fontFamily: r('font'),
    display: 'flex', flexDirection: 'column', gap: GAP,
    transition: `all ${FADE_MS}ms ${r('ease')}`,
    opacity: disabled ? BUSY_OPACITY : 1,
  };
}
